import { collection, getDocs, setDoc, doc, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../../src/firebase.js';

const LS_COLLAB = 'fg_fund_collaborators';

function readLocal() {
    try {
        return JSON.parse(localStorage.getItem(LS_COLLAB) || '{}');
    } catch {
        return {};
    }
}

function writeLocal(data) {
    localStorage.setItem(LS_COLLAB, JSON.stringify(data));
}

export function generateInviteToken() {
    return `inv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getCollaboratorsLocal(campaignId) {
    return readLocal()[campaignId] || [];
}

export async function getCollaborators(campaignId) {
    const local = readLocal()[campaignId] || [];
    try {
        const snap = await getDocs(
            query(collection(db, 'fund_campaign_collaborators'), where('campaignId', '==', campaignId))
        );
        const remote = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const byEmail = new Map();
        [...local, ...remote].forEach((c) => byEmail.set(c.email?.toLowerCase(), c));
        return [...byEmail.values()];
    } catch {
        return local;
    }
}

export async function inviteCollaborator(campaignId, { email, role, ownerUid }) {
    const normalized = String(email).trim().toLowerCase();
    if (!normalized) throw new Error('Email required');

    const entry = {
        campaignId,
        email: normalized,
        role: role === 'viewer' ? 'viewer' : 'editor',
        status: 'pending',
        inviteToken: generateInviteToken(),
        ownerUid: ownerUid || null,
        invitedAt: Date.now(),
    };

    const all = readLocal();
    const list = all[campaignId] || [];
    const idx = list.findIndex((c) => c.email === normalized);
    if (idx >= 0) list[idx] = { ...list[idx], ...entry };
    else list.push(entry);
    all[campaignId] = list;
    writeLocal(all);

    try {
        const ref = doc(collection(db, 'fund_campaign_collaborators'));
        await setDoc(ref, { ...entry, createdAt: serverTimestamp() }, { merge: true });
        entry.id = ref.id;
    } catch (err) {
        console.warn('[fundTeam] Firestore invite failed', err);
    }
    return entry;
}

export async function revokeCollaborator(campaignId, email) {
    const normalized = email.toLowerCase();
    const all = readLocal();
    all[campaignId] = (all[campaignId] || []).filter((c) => c.email !== normalized);
    writeLocal(all);
    try {
        const snap = await getDocs(
            query(
                collection(db, 'fund_campaign_collaborators'),
                where('campaignId', '==', campaignId),
                where('email', '==', normalized)
            )
        );
        await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    } catch (_) { /* ignore */ }
}

export function canManageCampaign(project, user) {
    if (!user) return false;
    if (project.ownerUid === user.uid) return true;
    const collabs = readLocal()[project.id] || [];
    return collabs.some(
        (c) => c.email === user.email?.toLowerCase() && c.status === 'accepted' && c.role === 'editor'
    );
}

export function canViewAnalytics(project, user) {
    if (!user) return false;
    if (project.ownerUid === user.uid) return true;
    const collabs = readLocal()[project.id] || [];
    return collabs.some(
        (c) => c.email === user.email?.toLowerCase() && c.status === 'accepted'
    );
}

export async function acceptInvite(campaignId, inviteToken, uid) {
    const all = readLocal();
    const list = all[campaignId] || [];
    const idx = list.findIndex((c) => c.inviteToken === inviteToken);
    if (idx < 0) return false;
    list[idx].status = 'accepted';
    list[idx].acceptedUid = uid;
    all[campaignId] = list;
    writeLocal(all);
    return true;
}
